import React, { useState } from 'react';
import PageNavbar from '../../components/feature/PageNavbar';
import Footer from '../home/components/Footer';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

const sections = [
  { id: 'verantwortlicher', label: 'Verantwortlicher' },
  { id: 'uebersicht', label: 'Übersicht der Verarbeitungen' },
  { id: 'rechtsgrundlagen', label: 'Rechtsgrundlagen' },
  { id: 'sicherheit', label: 'Sicherheitsmaßnahmen' },
  { id: 'rechte', label: 'Rechte der Betroffenen' },
  { id: 'cookies', label: 'Cookies' },
  { id: 'hosting', label: 'Hosting & CDN' },
  { id: 'kontaktformular', label: 'Kontaktformular' },
  { id: 'newsletter', label: 'Newsletter' },
  { id: 'webanalyse', label: 'Webanalyse' },
  { id: 'aenderungen', label: 'Änderungen' },
];

interface AccordionProps {
  title: string;
  id: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function Accordion({ title, id, children, defaultOpen = false }: AccordionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div id={id} className="scroll-mt-28 border border-neutral-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 bg-brand-beige-light/30 hover:bg-brand-beige-light/50 transition-colors cursor-pointer"
      >
        <h4 className="text-lg font-semibold text-brand-black text-left">{title}</h4>
        <div className="w-6 h-6 flex items-center justify-center shrink-0">
          <i
            className={`ri-${open ? 'subtract' : 'add'}-line text-brand-grey transition-transform duration-300`}
          ></i>
        </div>
      </button>
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-5 space-y-3">{children}</div>
      </div>
    </div>
  );
}

export default function DatenschutzPage() {
  // SEO Hook direkt im Component Body aufrufen
  useSEO({
    title: 'Datenschutzerklärung - BR Webdesign & AI Solutions Titisee-Neustadt',
    description:
      'Datenschutzerklärung von BR Webdesign & AI Solutions gemäß DSGVO. Informationen zur Datenverarbeitung – Webdesign Titisee-Neustadt.',
    keywords:
      'Datenschutzerklärung, DSGVO, Datenschutz, BR Webdesign, Titisee-Neustadt',
    canonical: '/datenschutz',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          'Datenschutzerklärung',
          'Datenschutzerklärung von BR Webdesign & AI Solutions gemäß DSGVO',
          '/datenschutz'
        ),
      ],
    },
  });

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white font-inter">
      <PageNavbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-brand-beige text-brand-brown text-xs font-semibold tracking-wide uppercase">
              DSGVO
            </span>
            <span className="text-xs text-brand-grey">
              Zuletzt aktualisiert:{' '}
              {new Date().toLocaleDateString('de-DE', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-caprasimo text-brand-black mb-4">
            Datenschutzerklärung
          </h1>
          <p className="text-brand-grey text-base max-w-2xl">
            Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Nachfolgend informieren wir
            Sie über die Erhebung personenbezogener Daten bei Nutzung unserer Website.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-6">
        <div className="max-w-5xl mx-auto flex gap-16">
          {/* Sidebar */}
          <aside className="hidden lg:block w-56 shrink-0">
            <div className="sticky top-28">
              <nav className="flex flex-col gap-1">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className="text-left text-sm text-brand-grey hover:text-brand-black hover:font-medium transition-all py-2 px-3 rounded-md hover:bg-brand-beige-light/50 cursor-pointer whitespace-nowrap"
                  >
                    {s.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main */}
          <main className="flex-1 min-w-0 space-y-6">
            {/* Verantwortlicher */}
            <Accordion title="1. Verantwortlicher" id="verantwortlicher" defaultOpen>
              <p className="text-gray-700 leading-relaxed mb-4">
                Verantwortliche Stelle im Sinne der Datenschutzgesetze:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <p className="text-gray-800 font-medium mb-2">BR Webdesign & AI Solutions</p>
                <p className="text-gray-700">
                  Freiburger Str. 19<br />
                  79822 Titisee-Neustadt<br />
                  Deutschland
                </p>
              </div>
            </Accordion>

            {/* Übersicht */}
            <Accordion title="2. Übersicht der Verarbeitungen" id="uebersicht">
              <p className="text-sm text-brand-grey leading-relaxed">
                Die nachfolgende Übersicht fasst die Arten der verarbeiteten Daten und die Zwecke
                ihrer Verarbeitung zusammen:
              </p>
              <ul className="space-y-2 text-sm text-brand-grey">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Bestandsdaten:</strong> Namen, Adressen
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Kontaktdaten:</strong> E-Mail,
                    Telefonnummern
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Inhaltsdaten:</strong> Eingaben in
                    Kontaktformularen
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Nutzungsdaten:</strong> Besuchte Seiten,
                    Zugriffszeiten
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Meta-/Kommunikationsdaten:</strong>{' '}
                    IP-Adressen, Geräteinformationen
                  </span>
                </li>
              </ul>
            </Accordion>

            {/* Rechtsgrundlagen */}
            <Accordion title="3. Maßgebliche Rechtsgrundlagen" id="rechtsgrundlagen">
              <p className="text-sm text-brand-grey leading-relaxed">
                Im Folgenden erhalten Sie eine Übersicht der Rechtsgrundlagen der DSGVO, auf deren
                Basis wir personenbezogene Daten verarbeiten:
              </p>
              <ul className="space-y-2 text-sm text-brand-grey">
                <li className="flex items-start gap-2">
                  <i className="ri-article-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">
                      Einwilligung (Art. 6 Abs. 1 S. 1 lit. a DSGVO):
                    </strong>{' '}
                    Die betroffene Person hat ihre Einwilligung in die Verarbeitung der sie
                    betreffenden personenbezogenen Daten gegeben.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-article-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">
                      Vertragserfüllung (Art. 6 Abs. 1 S. 1 lit. b DSGVO):
                    </strong>{' '}
                    Die Verarbeitung ist für die Erfüllung eines Vertrags erforderlich.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-article-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">
                      Berechtigte Interessen (Art. 6 Abs. 1 S. 1 lit. f DSGVO):
                    </strong>{' '}
                    Die Verarbeitung ist zur Wahrung berechtigter Interessen erforderlich.
                  </span>
                </li>
              </ul>
            </Accordion>

            {/* Sicherheit */}
            <Accordion title="4. Sicherheitsmaßnahmen" id="sicherheit">
              <p className="text-sm text-brand-grey leading-relaxed">
                Wir treffen nach Maßgabe der gesetzlichen Vorgaben unter Berücksichtigung des
                Stands der Technik geeignete technische und organisatorische Maßnahmen, um ein dem
                Risiko angemessenes Schutzniveau zu gewährleisten.
              </p>
              <ul className="space-y-2 text-sm text-brand-grey">
                <li className="flex items-start gap-2">
                  <i className="ri-shield-check-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>SSL/TLS-Verschlüsselung der gesamten Website</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-shield-check-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>Regelmäßige Sicherheitsupdates und Backups</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-shield-check-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>Zugriffsbeschränkungen und Passwortschutz</span>
                </li>
              </ul>
            </Accordion>

            {/* Rechte */}
            <Accordion title="5. Rechte der betroffenen Personen" id="rechte">
              <p className="text-sm text-brand-grey leading-relaxed mb-2">
                Ihnen stehen als Betroffene nach der DSGVO verschiedene Rechte zu:
              </p>
              <ul className="space-y-2 text-sm text-brand-grey">
                <li className="flex items-start gap-2">
                  <i className="ri-user-settings-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Auskunftsrecht (Art. 15 DSGVO):</strong>{' '}
                    Sie haben das Recht, eine Bestätigung darüber zu verlangen, ob betreffende Daten
                    verarbeitet werden.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-user-settings-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Recht auf Berichtigung (Art. 16 DSGVO):</strong>{' '}
                    Sie haben das Recht, die Berichtigung unrichtiger Daten zu verlangen.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-user-settings-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Recht auf Löschung (Art. 17 DSGVO):</strong>{' '}
                    Sie haben das Recht, die Löschung Ihrer Daten zu verlangen.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-user-settings-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Recht auf Einschränkung (Art. 18 DSGVO):</strong>{' '}
                    Sie haben das Recht, die Einschränkung der Verarbeitung zu verlangen.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-user-settings-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">
                      Recht auf Datenübertragbarkeit (Art. 20 DSGVO):
                    </strong>{' '}
                    Sie haben das Recht, Ihre Daten in einem gängigen Format zu erhalten.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-user-settings-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Widerspruchsrecht (Art. 21 DSGVO):</strong>{' '}
                    Sie haben das Recht, jederzeit gegen die Verarbeitung Widerspruch einzulegen.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-user-settings-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Beschwerderecht:</strong> Sie haben das Recht,
                    sich bei einer Aufsichtsbehörde zu beschweren.
                  </span>
                </li>
              </ul>
            </Accordion>

            {/* Cookies */}
            <Accordion title="6. Einsatz von Cookies" id="cookies">
              <p className="text-sm text-brand-grey leading-relaxed">
                Cookies sind kleine Textdateien, die auf Ihrem Endgerät gespeichert werden. Wir
                verwenden ausschließlich technisch notwendige Cookies, die für den Betrieb der
                Website erforderlich sind.
              </p>
              <p className="text-sm text-brand-grey leading-relaxed">
                <strong className="text-brand-black">Technisch notwendige Cookies:</strong> Diese
                Cookies sind für die Grundfunktionen der Website erforderlich und können nicht
                deaktiviert werden. Sie werden in der Regel nur als Reaktion auf von Ihnen getätigte
                Aktionen gesetzt.
              </p>
              <p className="text-sm text-brand-grey leading-relaxed">
                <strong className="text-brand-black">Rechtsgrundlage:</strong> Berechtigte Interessen
                (Art. 6 Abs. 1 S. 1 lit. f DSGVO).
              </p>
              <p className="text-sm text-brand-grey leading-relaxed">
                Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert
                werden und Cookies nur im Einzelfall erlauben. Bei der Deaktivierung von Cookies kann
                die Funktionalität unserer Website eingeschränkt sein.
              </p>
            </Accordion>

            {/* Hosting */}
            <Accordion title="7. Bereitstellung des Onlineangebotes und Webhosting" id="hosting">
              <p className="text-sm text-brand-grey leading-relaxed mb-3">
                Wir verarbeiten die Daten der Nutzer, um ihnen unsere Online-Dienste zur Verfügung
                stellen zu können. Zu diesem Zweck verarbeiten wir die IP-Adresse des Nutzers, die
                notwendig ist, um die Inhalte und Funktionen unserer Online-Dienste an den Browser
                bzw. das Endgerät der Nutzer zu übermitteln.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <i className="ri-server-line text-blue-600 text-lg mt-0.5 shrink-0"></i>
                  <div>
                    <p className="text-sm text-blue-900 font-medium mb-2">Hosting-Anbieter: Readdy.ai</p>
                    <p className="text-xs text-blue-800 leading-relaxed mb-2">
                      Diese Website wird über die Plattform Readdy.ai bereitgestellt. Dabei werden Daten 
                      vorübergehend auf Servern von Readdy.ai zwischengespeichert, die sich in verschiedenen 
                      Ländern befinden können (u.a. USA, EU-Raum).
                    </p>
                    <p className="text-xs text-blue-800 leading-relaxed">
                      <strong>Zwischenspeicherung:</strong> Zur Optimierung der Ladezeiten und Verfügbarkeit 
                      werden Website-Inhalte temporär auf Content Delivery Network (CDN) Servern in verschiedenen 
                      geografischen Regionen gespeichert. Dies dient ausschließlich der technischen Bereitstellung 
                      und Beschleunigung der Website.
                    </p>
                  </div>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-brand-grey">
                <li className="flex items-start gap-2">
                  <i className="ri-server-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Verarbeitete Datenarten:</strong>{' '}
                    Nutzungsdaten, Meta-/Kommunikationsdaten, Inhaltsdaten
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-server-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Betroffene Personen:</strong> Nutzer unserer
                    Website
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-server-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Zweck:</strong> Bereitstellung der Website, 
                    Sicherheit und Optimierung der Ladezeiten
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-server-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Rechtsgrundlage:</strong> Berechtigte Interessen
                    (Art. 6 Abs. 1 S. 1 lit. f DSGVO)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-server-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Speicherdauer:</strong> Die Daten werden nur 
                    so lange gespeichert, wie es für die Bereitstellung der Website erforderlich ist
                  </span>
                </li>
              </ul>
            </Accordion>

            {/* Kontaktformular */}
            <Accordion title="8. Kontaktaufnahme" id="kontaktformular">
              <p className="text-sm text-brand-grey leading-relaxed">
                Bei der Kontaktaufnahme mit uns (z.B. per Kontaktformular, E-Mail oder Telefon) werden
                die Angaben des Nutzers zur Bearbeitung der Kontaktanfrage und deren Abwicklung
                verarbeitet.
              </p>
              <ul className="space-y-2 text-sm text-brand-grey">
                <li className="flex items-start gap-2">
                  <i className="ri-mail-send-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Verarbeitete Datenarten:</strong> Bestandsdaten,
                    Kontaktdaten, Inhaltsdaten
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-mail-send-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Zweck:</strong> Bearbeitung von
                    Kontaktanfragen und Kommunikation
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-mail-send-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Rechtsgrundlage:</strong> Vertragserfüllung und
                    vorvertragliche Anfragen (Art. 6 Abs. 1 S. 1 lit. b DSGVO), Berechtigte Interessen
                    (Art. 6 Abs. 1 S. 1 lit. f DSGVO)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-mail-send-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Speicherdauer:</strong> Die Daten werden
                    gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr
                    erforderlich sind.
                  </span>
                </li>
              </ul>
            </Accordion>

            {/* Newsletter */}
            <Accordion title="9. Newsletter" id="newsletter">
              <p className="text-sm text-brand-grey leading-relaxed">
                Wir versenden Newsletter nur mit der Einwilligung der Empfänger oder einer gesetzlichen
                Erlaubnis. Die Anmeldung zu unserem Newsletter erfolgt in einem sogenannten
                Double-Opt-In-Verfahren.
              </p>
              <ul className="space-y-2 text-sm text-brand-grey">
                <li className="flex items-start gap-2">
                  <i className="ri-newspaper-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Verarbeitete Datenarten:</strong> E-Mail-Adresse
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-newspaper-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Zweck:</strong> Versand von Informationen zu
                    Webdesign, SEO und AI
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-newspaper-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Rechtsgrundlage:</strong> Einwilligung (Art. 6
                    Abs. 1 S. 1 lit. a DSGVO)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-newspaper-line text-brand-green mt-0.5 shrink-0"></i>
                  <span>
                    <strong className="text-brand-black">Widerruf:</strong> Sie können den Empfang
                    jederzeit kündigen, z.B. über einen Link am Ende jedes Newsletters.
                  </span>
                </li>
              </ul>
            </Accordion>

            {/* Webanalyse */}
            <Accordion title="10. Webanalyse und Optimierung" id="webanalyse">
              <p className="text-sm text-brand-grey leading-relaxed">
                Wir setzen derzeit keine Webanalyse-Tools von Drittanbietern ein. Sollte sich dies
                ändern, werden wir diese Datenschutzerklärung entsprechend aktualisieren und Sie
                vorab informieren.
              </p>
              <p className="text-sm text-brand-grey leading-relaxed">
                Server-Logfiles werden vom Hosting-Anbieter automatisch erhoben und enthalten
                Informationen wie Browsertyp, Betriebssystem, Referrer-URL, IP-Adresse und Uhrzeit des
                Zugriffs. Diese Daten werden nicht mit anderen Datenquellen zusammengeführt.
              </p>
            </Accordion>

            {/* Änderungen */}
            <Accordion title="11. Änderung dieser Datenschutzerklärung" id="aenderungen">
              <p className="text-sm text-brand-grey leading-relaxed">
                Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den
                aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer
                Leistungen in der Datenschutzerklärung umzusetzen. Für Ihren erneuten Besuch gilt dann
                die neue Datenschutzerklärung.
              </p>
              <p className="text-sm text-brand-grey leading-relaxed">
                <strong className="text-brand-black">Stand:</strong>{' '}
                {new Date().toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </Accordion>
          </main>
        </div>
      </section>

      <Footer />
    </div>
  );
}
